import { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import useDefaultErrorSnackbar from '@/hooks/useDefaultErrorSnackbar';
import { useUpdateEventMutation } from '@/modules/events/graphql/mutations/generated/updateEvent';
import { UpdateEventInput } from '@/types/schema';

interface Props {
  eventId?: string;
}

const UPDATE_EVENT_API_VERSION = 2;

const useEventUpdate = ({ eventId }: Props) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const { showDefaultErrorSnackbar } = useDefaultErrorSnackbar();
  const [updatedEvent, executeUpdateEvent] = useUpdateEventMutation();

  const onUpdateEvent = useCallback(
    async (
      data: Partial<UpdateEventInput>,
      onSuccess?: (data: Partial<UpdateEventInput>) => void,
    ) => {
      if (!eventId) {
        return false;
      }

      const response = await executeUpdateEvent({
        input: { ...data, eventId, apiVersion: UPDATE_EVENT_API_VERSION },
      });

      if (!response.data?.updateEvent?.event?.id) {
        showDefaultErrorSnackbar();
      } else {
        if (onSuccess) {
          onSuccess(data);
        } else {
          showSnackbar({ message: t('Common:UpdateContentSuccess') });
        }
      }

      return true;
    },
    [eventId, executeUpdateEvent, showDefaultErrorSnackbar, showSnackbar, t],
  );

  return { fetching: updatedEvent.fetching, onUpdateEvent };
};

export default useEventUpdate;
